import { cn } from '@lib/theme';
import { motion, type HTMLMotionProps, type SVGMotionProps } from 'framer-motion';
import type { MouseEvent, ReactNode } from 'react';

import { useMenuContext } from './MenuContext';

function Path(props: SVGMotionProps<SVGPathElement>) {
  return <motion.path fill="transparent" strokeWidth="2" stroke="currentColor" strokeLinecap="round" {...props} />;
}

const TOP_CLOSED_PATH = 'M 2 2.5 L 20 2.5';
const TOP_OPEN_PATH = 'M 3 16.5 L 17 2.5';
const MIDDLE_PATH = 'M 2 9.423 L 20 9.423';
const BOTTOM_CLOSED_PATH = 'M 2 16.346 L 20 16.346';
const BOTTOM_OPEN_PATH = 'M 3 2.5 L 17 16.346';
const PATH_TRANSITION = { duration: 0.2, ease: 'easeInOut' as const };

type MenuButtonProps = Omit<HTMLMotionProps<'button'>, 'children'> & {
  className?: string;
  children?: ReactNode;
};

export default function MenuButton({ className, onClick, children, ...props }: MenuButtonProps) {
  const { isOpen, toggleMenu } = useMenuContext();

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    toggleMenu();
    onClick?.(event);
  };

  return (
    <motion.button
      type="button"
      className={cn(
        'text-primary inline-flex items-center justify-center rounded-full border border-transparent p-2 transition-colors duration-200',
        'hover:text-brand-peacock focus-visible:outline-ring focus-visible:outline-2 focus-visible:outline-offset-2',
        className
      )}
      onClick={handleClick}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      aria-label={isOpen ? 'Close menu' : 'Open menu'}
      aria-expanded={isOpen}
      {...props}
    >
      {children}
      <svg width="23" height="23" viewBox="0 0 23 23" aria-hidden="true" focusable="false">
        <Path
          initial={{ d: TOP_CLOSED_PATH }}
          animate={{ d: isOpen ? TOP_OPEN_PATH : TOP_CLOSED_PATH }}
          transition={PATH_TRANSITION}
        />
        <Path
          initial={{ opacity: 1, d: MIDDLE_PATH }}
          animate={{ opacity: isOpen ? 0 : 1, d: MIDDLE_PATH }}
          transition={{ duration: 0.1 }}
        />
        <Path
          initial={{ d: BOTTOM_CLOSED_PATH }}
          animate={{ d: isOpen ? BOTTOM_OPEN_PATH : BOTTOM_CLOSED_PATH }}
          transition={PATH_TRANSITION}
        />
      </svg>
    </motion.button>
  );
}
